// HLOS-300+: Pinia store para LX Layer (apps/ui-shell/src/lx/store.ts).
//
// HLOS-249 (Vue renderers) — bounded context espelhando companion-runtime skill §1.
// HLOS-254 (Pinia ↔ Pyodide bridge) — ações chamam lx_bridge.ts real
// HLOS-255 (LX trigger UX) — adiciona setProposal/deleteLXState
// HLOS-270 (TODOs cleanup) — adiciona lastError ref + completo API surface
//
// HLOS-283 (Bug B): store agora WIRE ao lx_bridge.ts real (Pyodide via
// initPyodide). Antes era placeholder (mock local). Cada ação chama a
// função da bridge passando:
//   - pyodide instance (de initPyodide, cache singleton)
//   - learner_id (de globalThis.__hlos_learner_id__ — setado pela vault)
//   - instance_id (do currentLX ou parâmetro)
//
// Constitucional:
// - CL-07 (vault local-first): bridge roda 100% no Pyodide local; zero rede
// - CL-08 (Ética fechada): learner_id obrigatório; se vazio, bloqueia (vault locked)
// - CL-09 strengthened (Explicabilidade): lastError rastreável; bridge errors
//   estruturados via ConstitutionalViolationError
// - Princípio XI (Companion ≠ LLM): copy de erro nunca cita provedor
// - CC-05 (zero penalidade): abandonLX/deleteLXState NUNCA atualizam θ_lat

import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { LXState, LXInteractionEvent, LXInteractionResponse, LXProposal } from './types'
import { initPyodide } from '@/pyodide/bridge'
import {
  startLX as bridgeStartLX,
  processLXInteraction as bridgeProcessLXInteraction,
  abandonLX as bridgeAbandonLX,
  deleteLXState as bridgeDeleteLX,
  deleteAllLXState as bridgeDeleteAllLX,
} from '@/pyodide/lx_bridge'

/**
 * HLOS-283 (Bug B): learner_id vem de globalThis.__hlos_learner_id__ —
 * setado pela vault após unlock (auth/__tests__/setup-mock-auth.ts).
 * Se vazio, vault está locked (CL-08 bloqueia).
 */
function getLearnerId(): string {
  const learnerId = (globalThis as { __hlos_learner_id__?: string })
    .__hlos_learner_id__
  return learnerId ?? ''
}

/**
 * HLOS-283 (Bug B): getPyodide lazy + singleton. initPyodide faz cache
 * interno (pyodide/bridge.ts), então é seguro chamar múltiplas vezes.
 */
async function getPyodide() {
  return await initPyodide()
}

export const useLXStore = defineStore('lx', () => {
  // ── State ─────────────────────────────────────────────────────────────
  const currentLX = ref<LXState | null>(null)
  const currentProposal = ref<LXProposal | null>(null)
  const isLoading = ref(false)
  const lastError = ref<string | null>(null)  // HLOS-270: tracking para UX

  // ── Actions ───────────────────────────────────────────────────────────

  /**
   * HLOS-254: cria nova LX no kernel Python via bridge.
   * Erros do bridge são capturados; learner_id ausente bloqueia (CL-08).
   */
  async function startLX(
    lxId: string,
    goal: string,
    lxType?: string,
    parameters?: Record<string, unknown>,
  ): Promise<LXState | null> {
    const learnerId = getLearnerId()
    if (!learnerId) {
      lastError.value = 'learner_id ausente (CL-08 — vault locked?)'
      return null
    }

    isLoading.value = true
    try {
      const py = await getPyodide()
      // Bridge valida lx_type enum e retorna LXState serializado do Python
      const newState = await bridgeStartLX(
        py,
        (lxType || 'question') as LXState['lx_type'],
        lxId,
        { goal, ...parameters },
        learnerId,
      )
      currentLX.value = newState
      return newState
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * HLOS-254: processa interação do learner com LX ativa.
   * Bridge retorna novo state + observation canônica.
   */
  async function processInteraction(
    instanceId: string,
    event: LXInteractionEvent,
  ): Promise<LXInteractionResponse | null> {
    const learnerId = getLearnerId()
    if (!learnerId) {
      lastError.value = 'learner_id ausente (CL-08)'
      return null
    }

    isLoading.value = true
    try {
      const py = await getPyodide()
      const response = await bridgeProcessLXInteraction(
        py,
        instanceId,
        event,
        learnerId,
      )
      currentLX.value = response.state
      return response
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * HLOS-254: marca LX como COMPLETED localmente (sem bridge call).
   * Útil para flow UX onde Companion confirma conclusão antes de sync kernel.
   * HLOS-283 (Bug B): limpa state local — proposal aceita já foi consumida,
   * LX concluída não precisa ficar visível. Kernel já tem o registro.
   */
  async function completeLX(_instanceId?: string): Promise<void> {
    isLoading.value = true
    try {
      if (currentLX.value) {
        currentLX.value = {
          ...currentLX.value,
          status: 'completed' as LXState['status'],
          completed_at: Date.now(),
        }
      }
      // Limpa state local — LX está done, proposal já foi consumida
      currentLX.value = null
      currentProposal.value = null
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * HLOS-254: marca LX como ABANDONED via bridge.
   * CC-05 Honrado: kernel NUNCA penaliza θ_lat (verificado no Python).
   * HLOS-283 (Bug B): retorna LXState atualizada para satisfazer type narrowing.
   */
  async function abandonLX(instanceId?: string): Promise<LXState | null> {
    const learnerId = getLearnerId()
    if (!learnerId) {
      lastError.value = 'learner_id ausente (CL-08)'
      return null
    }

    // HLOS-283: usa instanceId param OU pega do currentLX
    const targetInstanceId = instanceId ?? currentLX.value?.instance_id
    if (!targetInstanceId) {
      lastError.value = 'instance_id ausente (nenhuma LX ativa)'
      return null
    }

    isLoading.value = true
    try {
      const py = await getPyodide()
      const state = await bridgeAbandonLX(py, targetInstanceId, learnerId)
      currentLX.value = state
      return state
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * HLOS-255: define proposta do Companion (disclosure UX).
   * Não chama bridge — é UX state puro.
   */
  function setProposal(proposal: LXProposal | null): void {
    currentProposal.value = proposal
  }

  /**
   * HLOS-255 / LGPD Art. 18: deleta TODAS as LXs do learner (right to be forgotten).
   * CC-05 Honrado: NÃO afeta θ_lat. Limpa state local também.
   */
  async function deleteLXState(): Promise<{ deletedCount: number }> {
    const learnerId = getLearnerId()
    if (!learnerId) {
      lastError.value = 'learner_id ausente (CL-08)'
      return { deletedCount: 0 }
    }

    isLoading.value = true
    try {
      const py = await getPyodide()
      const result = await bridgeDeleteAllLX(py, learnerId)
      currentLX.value = null
      currentProposal.value = null
      return { deletedCount: result.deletedCount }
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
      return { deletedCount: 0 }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * HLOS-254: deleta LX específica (não all).
   * Útil se quiser deletar 1 LX sem perder histórico de outras.
   */
  async function deleteLXByInstance(instanceId: string): Promise<{ deleted: boolean }> {
    const learnerId = getLearnerId()
    if (!learnerId) {
      lastError.value = 'learner_id ausente (CL-08)'
      return { deleted: false }
    }

    isLoading.value = true
    try {
      const py = await getPyodide()
      const result = await bridgeDeleteLX(py, instanceId, learnerId)
      // Se deletamos o current LX, limpa
      if (currentLX.value?.instance_id === instanceId) {
        currentLX.value = null
      }
      return result
    } catch (e) {
      lastError.value = e instanceof Error ? e.message : String(e)
      return { deleted: false }
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    currentLX,
    currentProposal,
    isLoading,
    lastError,

    // Actions
    startLX,
    processInteraction,
    completeLX,
    abandonLX,
    setProposal,
    deleteLXState,
    deleteLXByInstance,
  }
})